import { useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { GreetWidget } from '@/components/GreetWidget/GreetWidget';
import { getWidgetSample } from '@/components/GreetWidget/widgetSamples';
import { loadPublicTemplate } from '@/lib/loadPublicTemplate';
import { resolveAccent } from '@/components/GreetWidget/widgetConfig';

// The page widget.js puts in an iframe on a customer's site. Query params mirror the
// script's data-* attributes; `wid` tags messages so several widgets can share a page.
const embedSearchSchema = z.object({
  theme: z.enum(['light', 'dark', 'auto']).optional().catch(undefined),
  size: z.enum(['compact', 'standard', 'large', 'wide']).optional().catch(undefined),
  mode: z.enum(['inline', 'floating']).optional().catch(undefined),
  accent: z.string().optional().catch(undefined),
  /** inline (default): the call opens inside the widget; new-tab: in a new tab. */
  open: z.enum(['inline', 'new-tab']).optional().catch(undefined),
  wid: z.string().optional().catch(undefined),
  /** Dev only: a fake session from widgetSamples, no API call. */
  sample: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/embed/$publicId')({
  component: EmbedPage,
  validateSearch: embedSearchSchema,
  loaderDeps: ({ search }) => ({ sample: search.sample }),
  loader: async ({ params, deps }) => {
    // Dev: /embed/x?sample=telehealth or /embed/sample-telehealth (what widget-test.html uses).
    const sample = import.meta.env.DEV
      ? getWidgetSample(deps.sample) ?? getWidgetSample(params.publicId.replace(/^sample-/, ''))
      : undefined;
    if (sample) return { vacancy: sample, isSample: true };
    return { ...(await loadPublicTemplate(params.publicId)), isSample: false };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.vacancy?.name ? `${loaderData.vacancy.name} · greet` : 'greet' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
});

type WidgetMessage =
  | { type: 'greet:ready'; accent: string }
  | { type: 'greet:resize'; height: number }
  | { type: 'greet:start'; roomPublicId: string }
  | { type: 'greet:call' }
  | { type: 'greet:call-cancel' }
  | { type: 'greet:error'; message: string }
  | { type: 'greet:close' };

function EmbedPage() {
  const { vacancy, isSample } = Route.useLoaderData();
  const { theme, size, mode, accent, wid, open } = Route.useSearch();
  const rootRef = useRef<HTMLDivElement>(null);

  const post = (message: WidgetMessage) => {
    if (window.parent === window) return;
    window.parent.postMessage({ ...message, id: wid ?? null, source: 'greet-widget' }, '*');
  };

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    let last = 0;
    const observer = new ResizeObserver(() => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      if (height !== last) {
        last = height;
        post({ type: 'greet:resize', height });
      }
    });
    observer.observe(node);
    // The launcher button (outside the iframe) takes the same colour as the card.
    post({ type: 'greet:ready', accent: resolveAccent(accent, vacancy?.company_info?.apply_page_theme) });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* The host page shows through around the card's rounded corners. */}
      {/* Same-origin view transition into the call page: both sides are black, so it reads as one shot. */}
      <style>{'html,body{background:transparent!important;margin:0}@view-transition{navigation:auto}'}</style>
      <div ref={rootRef}>
        <GreetWidget
          vacancy={vacancy}
          size={size}
          theme={theme}
          mode={mode}
          accent={accent}
          preview={isSample}
          openIn={open ?? 'inline'}
          onCall={() => post({ type: 'greet:call' })}
          onCallCancel={() => post({ type: 'greet:call-cancel' })}
          onStarted={(roomPublicId) => post({ type: 'greet:start', roomPublicId })}
          onError={(message) => post({ type: 'greet:error', message })}
          onClose={() => post({ type: 'greet:close' })}
        />
      </div>
    </>
  );
}
